/**
 * Step 12 — Customer Debrief (required free text).
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Step12Debrief({ workflow, requestId, onAdvance }) {
  const init = workflow?.step_12 || {};
  const [notes, setNotes] = useState(init.debrief_notes || "");
  const [dryingExplained, setDryingExplained] = useState(!!init.drying_process_explained);
  const [insuranceDiscussed, setInsuranceDiscussed] = useState(!!init.insurance_discussed);
  const [questions, setQuestions] = useState(init.customer_questions || "");
  const [special, setSpecial] = useState(init.special_instructions || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!notes.trim()) return toast.error("Customer debrief notes are required");
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/12/save`, {
        debrief_notes: notes.trim(),
        drying_process_explained: dryingExplained,
        insurance_discussed: insuranceDiscussed,
        customer_questions: questions || null,
        special_instructions: special || null,
      });
      toast.success("Debrief saved");
      onAdvance(13);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-12-form">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 12 · Customer Debrief</h2>
      </div>

      <Card><CardContent className="pt-4 space-y-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">What was communicated on site</p>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Walk-through of findings, scope explained, next steps…"
                  className="w-full p-2 text-xs border rounded h-32" data-testid="step-12-notes" />
        <p className="text-[9px] text-rose-500 mt-1">Required</p>

        <label className="flex items-center gap-2" data-testid="step-12-drying-toggle">
          <input type="checkbox" checked={dryingExplained} onChange={e => setDryingExplained(e.target.checked)} />
          <span className="text-xs text-zinc-700">Drying process explained to customer</span>
        </label>
        <label className="flex items-center gap-2" data-testid="step-12-insurance-toggle">
          <input type="checkbox" checked={insuranceDiscussed} onChange={e => setInsuranceDiscussed(e.target.checked)} />
          <span className="text-xs text-zinc-700">Insurance involvement discussed</span>
        </label>

        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Customer questions / concerns</p>
          <textarea value={questions} onChange={e => setQuestions(e.target.value)}
                    className="w-full p-2 text-xs border rounded h-20"
                    data-testid="step-12-questions" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Special instructions</p>
          <textarea value={special} onChange={e => setSpecial(e.target.value)}
                    className="w-full p-2 text-xs border rounded h-20"
                    data-testid="step-12-special" />
        </div>
      </CardContent></Card>

      <Button onClick={save} disabled={saving} className="w-full h-12 bg-zinc-900 text-white"
              data-testid="step-12-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save & Continue</>}
      </Button>
    </div>
  );
}
